import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Star, MapPin, Award, X } from 'lucide-react';
import { Badge } from './ui/badge';

const ReviewsModal = ({ isOpen, onClose, provider }) => {
  if (!provider) return null;

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'fill-current text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {provider.profile_picture ? (
                <img
                  src={provider.profile_picture.startsWith('data:') ? provider.profile_picture : `${process.env.REACT_APP_BACKEND_URL}${provider.profile_picture}`}
                  alt={provider.name}
                  className="w-12 h-12 rounded-full object-cover border-2"
                  style={{borderColor: 'var(--brand-navy)'}}
                />
              ) : (
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold"
                  style={{background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-light) 100%)'}}
                >
                  {provider.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h2 className="text-xl font-bold" style={{color: 'var(--brand-navy)'}}>
                  Reviews for {provider.name}
                </h2>
                {provider.business_name && (
                  <p className="text-sm text-gray-600">{provider.business_name}</p>
                )}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Provider Summary */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-6 mb-3">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 fill-current text-yellow-400" />
              <span className="text-lg font-semibold" style={{color: 'var(--brand-red)'}}>
                {provider.avg_rating ? provider.avg_rating.toFixed(1) : '0.0'}
              </span>
              <span className="text-sm text-gray-600">
                ({provider.total_reviews} review{provider.total_reviews !== 1 ? 's' : ''})
              </span>
            </div>
            
            {provider.location && (
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <MapPin className="w-4 h-4" />
                <span>{provider.location}</span>
              </div>
            )}
            
            {provider.years_experience && (
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <Award className="w-4 h-4" />
                <span>{provider.years_experience}y exp</span>
              </div>
            )}
          </div>

          {/* Services */}
          {provider.services_provided && provider.services_provided.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {provider.services_provided.map((service, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {service}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Reviews List */}
        <div className="space-y-6">
          {provider.recent_reviews && provider.recent_reviews.length > 0 ? (
            provider.recent_reviews.map((review, index) => (
              <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {renderStars(review.rating)}
                    <span className="font-medium text-gray-700">
                      {review.rating}/5 stars
                    </span>
                  </div>
                  {review.created_at && (
                    <span className="text-sm text-gray-500">
                      {formatDate(review.created_at)}
                    </span>
                  )}
                </div>

                {review.service_used && (
                  <div className="mb-2">
                    <Badge variant="outline" className="text-xs">
                      {review.service_used}
                    </Badge>
                  </div>
                )}

                {review.review && (
                  <p className="text-gray-700 mb-3 leading-relaxed">
                    "{review.review}"
                  </p>
                )}

                {review.would_recommend !== undefined && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Would recommend:</span>
                    <Badge 
                      variant={review.would_recommend ? "default" : "secondary"}
                      className={`text-xs ${review.would_recommend ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
                    >
                      {review.would_recommend ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No reviews yet</h3>
              <p className="text-gray-500">This provider hasn't received any reviews yet.</p>
            </div>
          )}
        </div>

        {provider.recent_reviews && provider.recent_reviews.length > 0 && provider.total_reviews > provider.recent_reviews.length && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700 text-center">
              Showing {provider.recent_reviews.length} of {provider.total_reviews} reviews
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReviewsModal;